import { ListenableEvent } from "../../utils";
import { clone } from "../../utils/array";
import { forEachParallel } from "../../utils/async";
import { ERR_INVALID_ARGUMENT } from "../../utils/error";
import { SetPropertyValue } from "../cmd/SetPropertyValue";
import { DEFAULT_PROPERTYCLASS_KEY } from "./Property";
import { PropertyChangedResult } from "./PropertyProvider";
export const PropertyMergeMode = {
    Intersection: 0,
    Union: 1
};
export class MultiPropertyProvider {
    get providers() {
        return (this.mProviders);
    }
    get mergeMode() {
        return (this.mMergeMode);
    }
    constructor(pApp) {
        this.eventPropertiesChanged = new ListenableEvent(this);
        this.onPropertiesChanged = (pResult) => {
            if (pResult != null)
                this.propertiesChanged(pResult);
        };
        this.mApp = pApp;
        this.mProviders = [];
        this.mMergeMode = PropertyMergeMode.Intersection;
        this.clearPropertyList();
        this.mPropertyListNeedsUpdate = true;
        this.mPropertyValuesNeedUpdate = true;
        this.mPropertyChangeDepth = 0;
        this.mPropertyChangedState = PropertyChangedResult.Nothing;
        this.mPropertyListPromise = null;
    }
    setProviders(pProviders) {
        this.deregisterProviderEvents();
        this.mProviders = pProviders == null ? [] : clone(pProviders);
        this.registerProviderEvents();
        this.propertiesChanged(PropertyChangedResult.Providers);
    }
    setMergeMode(pMode) {
        if (this.mMergeMode !== pMode) {
            this.mMergeMode = pMode;
            this.propertiesChanged(PropertyChangedResult.Properties);
        }
    }
    getProperties(pProgressHandler) {
        const tPromise = this.updatePropertyList(pProgressHandler).then(() => {
            return (this.mProperties);
        });
        return (tPromise);
    }
    getPropertyClasses(pProgressHandler) {
        const tPromise = this.updatePropertyList(pProgressHandler).then(() => {
            return (this.mPropertyClasses);
        });
        return (tPromise);
    }
    /** Returning the same result as `Property.getChoices()` but with an abortable progress. */
    getPropertyChoices(pMultiProperty, pProgressHandler) {
        return (this.internalGetPropertyChoices(this.getSubProperties(pMultiProperty), pProgressHandler));
    }
    beginPropertyChange() {
        this.mPropertyChangeDepth++;
    }
    endPropertyChange() {
        this.mPropertyChangeDepth--;
        if (this.mPropertyChangeDepth <= 0) {
            this.mPropertyChangeDepth = 0;
            if (this.mPropertyChangedState !== PropertyChangedResult.Nothing)
                this.propertiesChanged(this.mPropertyChangedState);
        }
    }
    setPropertyValueUndoable(pMultiProperty, pValue, pProgressHandler) {
        return (this.setPropertyValue(pMultiProperty, pValue, true, pProgressHandler));
    }
    /**
     * Returnes true if the merged properties of the given multi property do not have an equal value.
     */
    isPropertyValueUndetermined(pMultiProperty) {
        this.updatePropertyValues();
        const tValue = this.mPropertyValueMap.get(pMultiProperty.key);
        return (tValue != null ? tValue.undetermined : false);
    }
    beginSetPropertyValue() {
        if (this.mApp != null)
            this.mApp.commands.beginUndoSection();
        this.beginPropertyChange();
    }
    endSetPropertyValue() {
        this.endPropertyChange();
        if (this.mApp != null)
            this.mApp.commands.endUndoSection();
    }
    finalizeSetPropertyValue() {
        return (Promise.resolve());
    }
    propertiesChanged(pChange) {
        if (pChange !== PropertyChangedResult.Nothing) {
            this.mPropertyValuesNeedUpdate = true;
            if (pChange > PropertyChangedResult.Value)
                this.mPropertyListNeedsUpdate = true;
            if (pChange > this.mPropertyChangedState)
                this.mPropertyChangedState = pChange;
        }
        if (this.mPropertyChangeDepth === 0) {
            this.eventPropertiesChanged.trigger(this.mPropertyChangedState);
            this.mPropertyChangedState = PropertyChangedResult.Nothing;
        }
    }
    registerProviderEvents() {
        for (const tProvider of this.mProviders)
            tProvider.eventPropertiesChanged.addListener(this.onPropertiesChanged);
    }
    deregisterProviderEvents() {
        for (const tProvider of this.mProviders)
            tProvider.eventPropertiesChanged.removeListener(this.onPropertiesChanged);
    }
    updatePropertyList(pProgressHandler) {
        //in case of reentrant return the same promise
        if (this.mPropertyListPromise != null)
            return (this.mPropertyListPromise);
        if (!this.mPropertyListNeedsUpdate)
            return (Promise.resolve());
        this.clearPropertyList();
        const tTempData = {
            properties: [],
            classes: [],
            addedClasses: new Set()
        };
        this.mPropertyListNeedsUpdate = false;
        const tTotal = this.mProviders.length;
        let tFinished = 0;
        let tAborted = false;
        let tFailed = false;
        if (pProgressHandler != null && tTotal > 0) {
            tAborted = !pProgressHandler(tFinished, tTotal);
        }
        this.mPropertyListPromise = forEachParallel(this.mProviders, async (pIndex, pProvider) => {
            if (tAborted || tFailed)
                return;
            await this.addProviderClasses(pProvider, tTempData);
            await this.addProviderProperties(pProvider, tTempData);
            tFinished++;
            if (pProgressHandler != null) {
                tAborted = tAborted || !pProgressHandler(tFinished, tTotal);
            }
        }, MultiPropertyProvider.MAX_CONCURRENT_TASKS).then(() => {
            this.mPropertyListPromise = null;
            if (this.mPropertyListNeedsUpdate) //list was changed while update is in progress, do it again
                return (this.updatePropertyList());
            else {
                this.buildPropertyList(tTempData);
                return (Promise.resolve());
            }
        }).catch((pError) => {
            tFailed = true;
            this.mPropertyListPromise = null;
            this.clearPropertyList();
            return (Promise.reject(pError));
        });
        return (this.mPropertyListPromise);
    }
    updatePropertyValues() {
        if (!this.mPropertyValuesNeedUpdate)
            return;
        this.mPropertyValuesNeedUpdate = false;
        for (const tMultiProperty of this.mProperties) {
            const tProperties = this.mPropertyMap.get(tMultiProperty.key);
            if (tProperties == null)
                continue;
            const tValue = tProperties[0].getValue();
            let tEqual = true;
            for (let i = 1; i < tProperties.length; ++i) {
                if (!tProperties[i].isValueEqual(tValue)) {
                    tEqual = false;
                    break;
                }
            }
            if (tEqual)
                this.mPropertyValueMap.set(tMultiProperty.key, { value: tValue, undetermined: false });
            else
                this.mPropertyValueMap.set(tMultiProperty.key, { value: null, undetermined: true });
        }
    }
    clearPropertyList() {
        this.mProperties = [];
        this.mPropertyClasses = [];
        this.mPropertyMap = new Map();
        this.mPropertyValueMap = new Map();
    }
    addProviderClasses(pProvider, pTemp) {
        const tPromise = pProvider.getPropertyClasses().then((pClasses) => {
            //merge equal classes to temp list
            if (pClasses != null) {
                for (const tClass of pClasses) {
                    if (!pTemp.addedClasses.has(tClass.key)) {
                        pTemp.addedClasses.add(tClass.key);
                        pTemp.classes.push(tClass);
                    }
                }
            }
        });
        return (tPromise);
    }
    addProviderProperties(pProvider, pTemp) {
        const tPromise = pProvider.getProperties().then((pProperties) => {
            //merge equal properties to map/temp list
            if (pProperties != null) {
                for (const tProperty of pProperties) {
                    let tProps = this.mPropertyMap.get(tProperty.key);
                    if (tProps == null) {
                        tProps = [];
                        this.mPropertyMap.set(tProperty.key, tProps);
                        pTemp.properties.push(tProperty.key);
                    }
                    tProps.push(tProperty);
                }
            }
        });
        return (tPromise);
    }
    buildPropertyList(pTemp) {
        var _a;
        const tUsedClasses = new Set();
        //build final property list
        const tProviderCount = this.mProviders.length;
        for (const tKey of pTemp.properties) {
            const tProperties = this.mPropertyMap.get(tKey);
            if (tProperties == null)
                continue;
            if (tProperties.length === tProviderCount || this.mMergeMode === PropertyMergeMode.Union) {
                const tMultiProp = this.buildProperty(tKey, tProperties);
                if (tMultiProp != null) {
                    tUsedClasses.add((_a = tMultiProp.class) !== null && _a !== void 0 ? _a : DEFAULT_PROPERTYCLASS_KEY);
                }
            }
        }
        this.mPropertyValuesNeedUpdate = true;
        this.updatePropertyValues();
        //build final classes list
        for (const tClass of pTemp.classes) {
            if (tUsedClasses.has(tClass.key)) {
                this.mPropertyClasses.push(tClass);
            }
        }
    }
    buildProperty(pKey, pProperties) {
        if (pProperties == null || pProperties.length === 0)
            return (null);
        const tMultiProperty = pProperties[0].clone();
        tMultiProperty._setProvider(this);
        //store property list as custom data
        tMultiProperty[MultiPropertyProvider.SUB_PROPERTIES] = pProperties;
        //the multi property itself is not undoable but the sub properties are
        //use setPropertyValueUndoable
        tMultiProperty.undoable = false;
        tMultiProperty.setValue = (pValue) => this.setPropertyValue(tMultiProperty, pValue, false);
        tMultiProperty.getValue = () => this.getPropertyValue(pKey);
        tMultiProperty.getChoices = () => this.internalGetPropertyChoices(pProperties);
        //merge states
        for (const tProperty of pProperties) {
            if (!tProperty.editable)
                tMultiProperty.editable = false;
            if (!tProperty.visible)
                tMultiProperty.visible = false;
            if (!tProperty.undoable)
                tMultiProperty.undoable = false;
        }
        this.mProperties.push(tMultiProperty);
        return (tMultiProperty);
    }
    async setPropertyValue(pMultiProperty, pValue, pUndoable, pProgressHandler) {
        if (pMultiProperty == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        const tProperties = this.getSubProperties(pMultiProperty);
        try {
            this.beginSetPropertyValue();
            const tTotal = tProperties.length;
            let tFinished = 0;
            let tAborted = false;
            let tFailed = false;
            if (pProgressHandler != null && tTotal > 0) {
                tAborted = !pProgressHandler(tFinished, tTotal);
            }
            const tResults = await forEachParallel(tProperties, async (pIndex, pProperty) => {
                let tResult = PropertyChangedResult.Nothing;
                if (tAborted || tFailed) {
                    return (tResult);
                }
                try {
                    if (pUndoable && this.mApp != null) {
                        const tCommand = new SetPropertyValue(this.mApp, pProperty, pValue);
                        await this.mApp.commands.executeCommand(tCommand);
                        tResult = tCommand.lastResult;
                    }
                    else {
                        tResult = await pProperty.setValue(pValue);
                    }
                }
                catch (pError) {
                    tFailed = true;
                    throw (pError);
                }
                tFinished++;
                if (pProgressHandler != null) {
                    tAborted = tAborted || !pProgressHandler(tFinished, tTotal);
                }
                return (tResult);
            }, MultiPropertyProvider.MAX_CONCURRENT_SET_PROPVALUE);
            //check if value change was successful
            let tResult = PropertyChangedResult.Nothing;
            if (tResults != null) {
                for (const tRes of tResults) {
                    if (tRes > tResult)
                        tResult = tRes;
                }
            }
            await this.finalizeSetPropertyValue();
            return (tResult);
        }
        finally {
            this.endSetPropertyValue();
        }
    }
    getPropertyValue(pKey) {
        //getPropertyValue is called while updating the property list
        if (this.mPropertyListPromise != null)
            return (null);
        this.updatePropertyValues();
        const tValue = this.mPropertyValueMap.get(pKey);
        return (tValue != null ? tValue.value : null);
    }
    async internalGetPropertyChoices(pProperties, pProgressHandler) {
        if (pProperties.length === 0)
            return (null);
        const tTotal = pProperties.length;
        let tFinished = 0;
        let tAborted = false;
        let tFailed = false;
        if (pProgressHandler != null && tTotal > 0) {
            tAborted = !pProgressHandler(tFinished, tTotal);
        }
        //if first property is not a choice list its assumed that all are not a choice list
        if (tAborted || !pProperties[0].choiceList)
            return (null);
        if (pProperties.length === 1)
            return (pProperties[0].getChoices());
        //get choice list from all properties
        const tResult = await forEachParallel(pProperties, async (pIndex, pProp) => {
            let tChoices = null;
            if (tAborted || tFailed) {
                return (tChoices);
            }
            try {
                tChoices = await pProp.getChoices();
            }
            catch (pError) {
                tFailed = true;
                throw (pError);
            }
            tFinished++;
            if (pProgressHandler != null) {
                tAborted = tAborted || !pProgressHandler(tFinished, tTotal);
            }
            return (tChoices);
        }, MultiPropertyProvider.MAX_CONCURRENT_TASKS);
        if (tAborted || tResult == null)
            return (null);
        //merge choices (intersection)
        const tChoicesList = new Array();
        const tChoicesCountMap = new Map();
        for (const tChoices of tResult) {
            if (tChoices == null)
                continue;
            for (const tChoice of tChoices) {
                const tCount = tChoicesCountMap.get(tChoice.value);
                if (tCount == null) {
                    tChoicesList.push(tChoice);
                    tChoicesCountMap.set(tChoice.value, 1);
                }
                else {
                    tChoicesCountMap.set(tChoice.value, tCount + 1);
                }
            }
        }
        const tMergedChoices = new Array();
        for (const tChoice of tChoicesList) {
            const tCount = tChoicesCountMap.get(tChoice.value);
            if (tCount === pProperties.length)
                tMergedChoices.push(tChoice);
        }
        return (tMergedChoices);
    }
    getSubProperties(pMultiProperty) {
        let tProperties = pMultiProperty[MultiPropertyProvider.SUB_PROPERTIES];
        if (tProperties == null)
            tProperties = [pMultiProperty]; //fallback, maybe one of the sub properties is changed directly
        return (tProperties);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
MultiPropertyProvider.SUB_PROPERTIES = "MultiPropertyProvider_subProperties";
MultiPropertyProvider.MAX_CONCURRENT_TASKS = 10;
MultiPropertyProvider.MAX_CONCURRENT_SET_PROPVALUE = 5;
